// English dictionary — keys must mirror ko.ts (compile-time enforced via TranslationKey)
import type { TranslationKey } from './ko'

export const en: Record<TranslationKey, string> = {
  // common
  'common.loading': 'Loading…',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.confirm': 'Confirm',
  'common.close': 'Close',
  'common.search': 'Search',
  'common.refresh': 'Refresh',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.theme.toggle': 'Toggle theme (current: {{theme}})',
  'common.menu.open': 'Open menu',
  'common.menu.close': 'Close menu',
  'common.logout': 'Log out',
  'common.lang.toggle': 'Toggle language (current: {{lang}})',
  'common.skipNav': 'Skip to main content',
  'common.units.count': '',
  'common.units.people': '',
  'common.units.ea': ' ea',
  'common.units.percent': '%',
  'common.units.minutes': 'min',
  'common.units.hours': 'h',
  'common.units.days': 'd',
  'common.empty': 'No data',
  'common.success': 'Success',
  'common.error': 'Failed',
  'common.notifications': 'Notifications',
  'common.start': 'Start',
  'common.complete': 'Complete',
  'common.create': 'Create',
  'common.issue': 'Issue',
  'common.send': 'Send',
  'common.run': 'Run',
  'common.calc': 'Calculate',
  'common.apply': 'Apply',
  'common.ack': 'Ack',
  'common.resolve': 'Resolve',
  'common.target': 'Target',
  'common.actual': 'Actual',
  'common.variance': 'Variance',
  'common.now': 'just now',
  'common.minutesAgo': '{{n}} min ago',
  'common.hoursAgo': '{{h}}h {{m}}m ago',
  'common.daysAgo': '{{n}}d ago',

  'brand.name': 'Com&Precision',
  'brand.suffix': 'MES',
  'brand.home': 'Com&Precision MES home',

  'nav.dashboard': 'Dashboard',
  'nav.andon': 'Andon',
  'nav.production': 'Orders',
  'nav.workOrders': 'Work Orders',
  'nav.equipmentData': 'Data Collect',
  'nav.inspection': 'Quality',
  'nav.spc': 'SPC',
  'nav.lots': 'LOT Trace',
  'nav.equipmentStatus': 'OEE Monitor',
  'nav.maintenance': 'Maintenance',
  'nav.labor': 'Labor',
  'nav.boms': 'BOM',
  'nav.schedule': 'Schedule',
  'nav.forecasting': 'Forecast',
  'nav.cost': 'Cost',
  'nav.inventory': 'Inventory',
  'nav.primary': 'Primary navigation',
  'nav.full': 'Full menu',
  'nav.allMenu': 'All menu items',
  'nav.group.operations': 'Operations',
  'nav.group.quality': 'Quality',
  'nav.group.equipment': 'Equipment',
  'nav.group.planning': 'Planning',
  'nav.group.master': 'Master',
  'nav.group.expand': 'Open submenu',
  'nav.audit': 'Audit Log',

  'login.title': 'Sign In',
  'login.subtitle': 'Authenticate to access Com&Precision MES.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign In',
  'login.submitting': 'Authenticating…',
  'login.success': 'Signed in successfully.',
  'login.failed': 'Sign-in failed.',
  'login.demo': 'Try with demo account',
  'login.demoHint': 'Read-only · cannot modify data',
  'login.demoSuccess': 'Signed in with demo account.',
  'demo.banner': 'Demo mode — read-only sandbox. Sales: admin@comnjeong.local',

  'dashboard.realtimeBadge': 'Live shopfloor',
  'dashboard.hero1': 'Every floor signal,',
  'dashboard.hero2': 'one screen.',
  'dashboard.intro': 'Synchronize Uniflow-ERP and PLC/3D gauges every 5 minutes — track OEE, defect rate, and work-order flow in real time.',
  'dashboard.avgOEE': 'Average OEE',
  'dashboard.avgOEE.note': 'Availability × Performance × Quality (latest report average)',
  'dashboard.runningWO': 'Running Work Orders',
  'dashboard.defectRate30d': '30-Day Defect Rate',
  'dashboard.productionOrders': 'Production Orders',
  'dashboard.productionOrders.note': 'Cumulative received, planned, and released from Uniflow-ERP',
  'dashboard.andonOpen': 'Andon Open',
  'dashboard.pmDue7d': 'PM due in 7 days: {{n}}',
  'dashboard.onDuty': 'On Duty',
  'dashboard.inventoryAccuracy': 'Inventory {{pct}}%',
  'dashboard.inventoryEmpty': 'No inventory counts yet (target 99.5%)',
  'dashboard.eqStatusTitle': 'Latest OEE by Equipment',
  'dashboard.swipeHint': 'Swipe horizontally',
  'dashboard.eqMetrics.operation': 'Avail.',
  'dashboard.eqMetrics.performance': 'Perf.',
  'dashboard.eqMetrics.quality': 'Quality',

  'trust.iso': 'Designed for ISO 27001',
  'trust.encryption': 'Encryption at rest & transit',
  'trust.audit': '100% audit logged',
  'trust.online': '{{n}} operators online',

  'toast.unauthorized': 'Authentication required.',
  'toast.forbidden': 'No permission.',
  'toast.networkError': 'Network error',
  'toast.fetchFailed': 'Failed to load data.',
  'toast.actionFailed': 'Action failed',
  'toast.saved': 'Saved.',
  'toast.created': 'Created.',
  'toast.deleted': 'Deleted.',

  'lang.ko': '한국어',
  'lang.en': 'English',

  'acct.title': 'Account Settings',
  'acct.intro': 'View your profile and change password. Changing password immediately logs out all other sessions.',
  'acct.profile.title': 'Profile',
  'acct.profile.email': 'Email',
  'acct.profile.name': 'Name',
  'acct.profile.roles': 'Roles',
  'acct.profile.tenant': 'Tenant',
  'acct.pw.title': 'Change Password',
  'acct.pw.current': 'Current password',
  'acct.pw.new': 'New password',
  'acct.pw.confirm': 'Confirm new password',
  'acct.pw.policy': 'At least 12 chars, 3+ classes of: upper / lower / digit / symbol.',
  'acct.pw.mismatch': 'Password confirmation does not match.',
  'acct.pw.same': 'New password must differ from current.',
  'acct.pw.submit': 'Change Password',
  'acct.pw.success': 'Password changed. All other sessions have been logged out.',
  'acct.pw.strengthLow': 'Weak',
  'acct.pw.strengthMid': 'Adequate',
  'acct.pw.strengthHigh': 'Strong',
  'nav.account': 'Account',

  'po.title': 'Production Orders',
  'po.intro': 'Production orders received from Uniflow-ERP. 5-minute auto-receive plus manual trigger supported.',
  'po.receive': 'Receive Orders',
  'po.received': '{{n}} production orders received.',
  'po.empty': 'No production orders received yet.',
  'po.emptyHint': 'Uniflow-ERP auto-receives every 5 minutes, or trigger manually with the "Receive Orders" button above.',
  'po.col.id': 'Order ID',
  'po.col.item': 'Item',
  'po.col.quantity': 'Qty',
  'po.col.due': 'Due Date',
  'po.col.priority': 'Priority',
  'po.col.status': 'Status',
  'po.aria.table': 'Production orders',
  'po.noWritePerm': 'No write permission.',

  'wo.title': 'Work Orders',
  'wo.new': 'New Work Order',
  'wo.field.poId': 'Production Order ID',
  'wo.field.line': 'Line',
  'wo.field.equipment': 'Equipment',
  'wo.field.worker': 'Worker',
  'wo.field.startDate': 'Start',
  'wo.field.endDate': 'End',
  'wo.created': 'Work order created.',
  'wo.empty': 'No work orders issued yet.',
  'wo.emptyHint': 'Use the "New Work Order" form above to issue one.',
  'wo.col.id': 'Work Order',
  'wo.col.poId': 'Prod. Order',
  'wo.col.item': 'Item',
  'wo.col.line': 'Line',
  'wo.col.equipment': 'Equipment',
  'wo.col.worker': 'Worker',
  'wo.col.period': 'Period',
  'wo.col.status': 'Status',
  'wo.aria.table': 'Work orders',

  'eqd.title': 'Equipment Data Collection',
  'eqd.intro': 'OPC-UA · Modbus TCP · MELSEC · S7 gateways stream data within 1 second.',
  'eqd.panel': 'Realtime Transmission Panel',
  'eqd.selectEq': 'Select Equipment',
  'eqd.send': 'Send {{status}}',
  'eqd.sent': '{{eq}} → {{status}} data sent.',
  'eqd.registered': 'Registered Equipment',
  'eqd.col.id': 'Equipment ID',
  'eqd.col.name': 'Name',
  'eqd.col.type': 'Type',
  'eqd.col.protocol': 'Protocol',
  'eqd.aria.list': 'Equipment list',

  'insp.title': 'Quality Inspection',
  'insp.intro': 'Measurements and pass/fail judgments auto-collected from Mitutoyo / Zeiss 3D gauges.',
  'insp.manual': 'Manual Entry',
  'insp.field.wo': 'Work Order',
  'insp.field.measurement': 'Measurement (mm)',
  'insp.field.judgment': 'Judgment',
  'insp.saved': 'Inspection {{judgment}} saved',
  'insp.empty': 'No inspection data collected yet.',
  'insp.emptyHint': 'Auto-collected from 3D gauges, or save via the "Manual Entry" form above.',
  'insp.col.id': 'Inspection ID',
  'insp.col.wo': 'Work Order',
  'insp.col.measurement': 'Measurement',
  'insp.col.judgment': 'Judgment',
  'insp.col.collectedAt': 'Collected',
  'insp.aria.table': 'Inspection data',

  'eqs.title': 'Equipment OEE Monitor',
  'eqs.intro': 'Latest OEE = Availability × Performance × Quality. Auto-refresh every 15 seconds.',
  'eqs.metric.operation': 'Availability',
  'eqs.metric.performance': 'Performance',
  'eqs.metric.quality': 'Quality',
  'eqs.metric.oee': 'OEE',
  'eqs.aria.cards': 'Equipment OEE cards',

  'andon.title': 'Andon Board',
  'andon.intro': 'Open alarms · severity color-coded · auto-refresh every 15 seconds',
  'andon.autoRefresh': 'Auto refresh',
  'andon.kpi.open': 'Open',
  'andon.kpi.mttr': 'Avg Resolve Time (7d)',
  'andon.kpi.resolved': 'Resolved (7d)',
  'andon.open.title': 'Open Alarms',
  'andon.empty': 'No open alarms.',
  'andon.ackedBy': 'Acked by {{user}} · {{at}}',
  'andon.recent.title': 'Resolved in last 24h',
  'andon.modal.title': 'Resolve Alarm',
  'andon.modal.note': 'Resolution note (required)',
  'andon.modal.placeholder': 'e.g. Coolant refilled, motor back to normal',
  'andon.modal.action': 'Resolve',
  'andon.acked': 'Acknowledged',
  'andon.resolved': 'Resolved',

  'spc.title': 'SPC (X̄–R)',
  'spc.intro': 'Mean/range with 3σ control limits (UCL/LCL) and capability (Cp/Cpk) computed from subgroups (n={{n}}).',
  'spc.selectCh': 'Select characteristic',
  'spc.spec': 'Spec',
  'spc.ooc': '{{n}} out-of-control',
  'spc.cpkGrade.excellent': 'Excellent',
  'spc.cpkGrade.adequate': 'Adequate',
  'spc.cpkGrade.marginal': 'Marginal',
  'spc.cpkGrade.inadequate': 'Inadequate',
  'spc.chart.xbar': 'X̄ Chart',
  'spc.chart.r': 'R Chart',
  'spc.ooc.title': 'Out-of-Control Details',
  'spc.ooc.subgroup': 'subgroup #{{n}}',
  'spc.warnInsufficient': 'Need ≥ 2 subgroups',

  'lot.title': 'LOT Traceability',
  'lot.intro': 'From a single product LOT, trace back to material LOTs, work orders, equipment, inspections, and co-occurring alarms in under 5 seconds.',
  'lot.search': 'Search LOT / item',
  'lot.empty': 'No matching product LOT.',
  'lot.col.lot': 'Product LOT',
  'lot.col.item': 'Item',
  'lot.col.qty': 'Qty',
  'lot.col.wo': 'Work Order',
  'lot.col.equipment': 'Equipment',
  'lot.col.producedAt': 'Produced',
  'lot.col.quality': 'Quality',
  'lot.action.trace': 'Trace',
  'lot.traceDone': 'Trace done ({{ms}}ms)',
  'lot.section.material': 'Consumed material LOTs ({{n}})',
  'lot.section.materialEmpty': 'No genealogy data',
  'lot.section.events': 'Inspections ({{insp}}) / co-alarms ({{alarm}})',
  'lot.section.inspectionEmpty': 'No inspection records',
  'lot.section.alarmEmpty': 'No co-occurring alarms',
  'lot.meta.item': 'Item',
  'lot.meta.qty': 'Qty',
  'lot.meta.equipment': 'Equipment',
  'lot.meta.line': 'Line',
  'lot.meta.worker': 'Worker',
  'lot.meta.produced': 'Produced',

  'maint.title': 'Maintenance',
  'maint.intro': 'Preventive, corrective, predictive, and precision maintenance with plans and history. MTBF · MTTR · Availability auto-computed from the last 30 days.',
  'maint.kpi.availability': 'Availability (30d)',
  'maint.kpi.faultless': 'Faultless',
  'maint.kpi.failures': '{{n}} failure(s)',
  'maint.plans.title': 'Preventive Maintenance Plans',
  'maint.plans.empty': 'No active maintenance plans.',
  'maint.col.equipment': 'Equipment',
  'maint.col.planName': 'Plan',
  'maint.col.type': 'Type',
  'maint.col.cycle': 'Cycle',
  'maint.col.estimated': 'Est.',
  'maint.col.lastDone': 'Last Done',
  'maint.col.due': 'Due',
  'maint.due.today': 'Today',
  'maint.due.future': 'in {{n}}d',
  'maint.due.overdue': '{{n}}d overdue',
  'maint.open.title': 'Open / Scheduled ({{n}})',
  'maint.open.empty': 'No pending work.',
  'maint.col.moId': 'Order',
  'maint.col.status': 'Status',
  'maint.col.assignee': 'Assignee',
  'maint.col.problem': 'Symptom / scheduled',
  'maint.recent.title': 'Recently Completed ({{n}})',
  'maint.col.completed': 'Completed',
  'maint.col.downtime': 'Downtime',
  'maint.col.resolution': 'Resolution',
  'maint.startDone': 'Work started',
  'maint.completeDone': 'Work completed',
  'maint.modal.complete': 'Complete — {{id}}',
  'maint.modal.resolution': 'Resolution (required)',
  'maint.modal.resolutionPh': 'e.g. Bearing replaced + lubricant refilled',
  'maint.modal.downtime': 'Downtime (min) — set 0 for preventive',
  'maint.modal.downtimePh': 'e.g. 75',
  'maint.modal.action': 'Complete',

  'labor.title': 'Labor',
  'labor.intro': 'Worker master, shift definitions, clock-in/out — foundation for work-order assignment and safety.',
  'labor.kpi.onDuty': 'Currently On Duty',
  'labor.kpi.onDutyHint': '{{pct}}% of {{total}} active workers',
  'labor.kpi.workers': 'Workers',
  'labor.kpi.shifts': 'Shifts',
  'labor.workers.title': 'Workers',
  'labor.col.id': 'ID',
  'labor.col.name': 'Name',
  'labor.col.role': 'Role',
  'labor.col.department': 'Department',
  'labor.col.certs': 'Certs',
  'labor.col.currentStatus': 'Status',
  'labor.onDuty.duration': 'On duty · {{dur}}',
  'labor.offDuty': 'off duty',
  'labor.clockIn': 'Clock In',
  'labor.clockOut': 'Clock Out',
  'labor.clockedIn': '{{id}} clocked in',
  'labor.clockedOut': '{{id}} clocked out',
  'labor.shifts.title': 'Shift Definitions',
  'labor.attendance.title': 'Recent Attendance',
  'labor.attendance.empty': 'No attendance records',
  'labor.modal.title': 'Pick Shift — {{id}}',
  'labor.modal.hint': 'Select a shift to clock in (optional).',
  'labor.modal.skip': 'Clock in without shift',

  'bom.title': 'BOM (Bill of Materials)',
  'bom.intro': 'Component composition and quantities per item (scrap included). Enter a production quantity to instantly compute material requirements.',
  'bom.list.title': 'Active BOMs',
  'bom.list.empty': 'No active BOMs.',
  'bom.detail.itemCount': '{{n}} components · base {{qty}} {{uom}}',
  'bom.detail.base': 'Base {{qty}} {{uom}}',
  'bom.items.title': 'Components ({{n}})',
  'bom.col.position': '#',
  'bom.col.component': 'Component',
  'bom.col.qtyPer': 'Qty / unit',
  'bom.col.uom': 'UoM',
  'bom.col.scrap': 'Scrap %',
  'bom.explode.title': 'Requirements (explode)',
  'bom.explode.qty': 'Produce qty ({{uom}})',
  'bom.explode.calc': 'Compute',
  'bom.explode.result': 'Materials required to produce {{qty}} {{uom}} of {{item}}:',
  'bom.col.gross': 'Gross',
  'bom.col.scrapQty': 'Scrap',
  'bom.col.required': 'Required',

  'sched.title': 'Production Schedule (Gantt)',
  'sched.intro': 'Work orders by line over time. Click a bar to adjust priority (insert urgent jobs).',
  'sched.days': '{{n}}d',
  'sched.empty': 'No scheduled work in this window.',
  'sched.legend.urgent': 'Urgent priority 1-2',
  'sched.legend.hint': 'Click bar → adjust priority',
  'sched.modal.priority': 'Priority (1 = most urgent, 9 = lowest)',
  'sched.modal.urgent': 'Urgent (1)',
  'sched.adjusted': '{{id}} priority → {{prio}}',

  'fc.title': 'AI Demand Forecast',
  'fc.intro': 'Forecast the next N days from historical demand. Compare SMA · EMA · Holt-Winters (trend + weekly seasonality).',
  'fc.field.item': 'Item',
  'fc.field.algo': 'Algorithm',
  'fc.field.horizon': 'Horizon (days)',
  'fc.run': 'Run Forecast',
  'fc.empty': 'Select an item and algorithm, then click "Run Forecast".',
  'fc.algo.sma': 'SMA (Simple Moving Average)',
  'fc.algo.ema': 'EMA (Exponential Moving Average)',
  'fc.algo.hw': 'Holt-Winters (trend + seasonality)',
  'fc.kpi.mape': 'Forecast Accuracy (MAPE)',
  'fc.kpi.mape.note': 'Lower is better · holdout validation',
  'fc.kpi.recommended': 'Recommended Order Qty',
  'fc.kpi.recommended.note': 'Lead time {{lead}}d + safety {{safety}}',
  'fc.kpi.cost': 'Standard Cost',
  'fc.chart.window': '{{hist}}d history · {{horizon}}d forecast',
  'fc.chart.legend.actual': 'Actual',
  'fc.chart.legend.forecast': 'Forecast',
  'fc.chart.legend.ci': '95% CI',
  'fc.detail.title': 'Forecast Detail',
  'fc.col.date': 'Date',
  'fc.col.forecast': 'Forecast',
  'fc.col.lower': 'Lower (95%)',
  'fc.col.upper': 'Upper (95%)',
  'fc.runDone': 'Forecast complete (MAPE {{mape}}%)',
  'fc.fail': 'Forecast failed',
  'fc.mode.single': 'Single',
  'fc.mode.compare': 'Compare',
  'fc.compare.run': 'Compare Models',
  'fc.compare.bestPick': 'Auto-pick (best)',
  'fc.compare.title': 'Algorithm Comparison — MAPE Ranking',
  'fc.compare.col.rank': 'Rank',
  'fc.compare.col.algo': 'Algorithm',
  'fc.compare.col.mape': 'MAPE',
  'fc.compare.col.mae': 'MAE',
  'fc.compare.col.rmse': 'RMSE',
  'fc.compare.bestBadge': 'BEST',
  'fc.compare.done': 'Comparison done — {{best}} selected (MAPE {{mape}}%)',
  'fc.compare.legend.actual': 'Actual',
  'fc.compare.legend.best': '{{name}} (best)',
  'fc.compare.legend.other': 'Other algorithms',

  'cost.title': 'Cost Roll-up',
  'cost.intro': 'Multi-level BOM explode × material standard cost → product standard cost. Sub-assemblies are decomposed into child materials to avoid double counting.',
  'cost.field.topItem': 'Top Item',
  'cost.field.qty': 'Produce Qty',
  'cost.run': 'Compute Cost',
  'cost.kpi.produce': 'Produce Qty',
  'cost.kpi.total': 'Standard Cost (total)',
  'cost.kpi.unit': 'Unit Cost',
  'cost.kpi.margin': 'List - Actual Cost',
  'cost.kpi.standardCost': 'List price {{cost}}',
  'cost.missing.title': '{{n}} materials missing cost',
  'cost.missing.note': 'These materials are excluded from the rollup because item_master.standard_cost is empty.',
  'cost.tree.title': 'Multi-level BOM Decomposition ({{n}})',
  'cost.col.component': 'Component',
  'cost.col.required': 'Required',
  'cost.col.unitCost': 'Unit Cost',
  'cost.col.amount': 'Amount',
  'cost.col.kind': 'Kind',
  'cost.col.total': 'Total',
  'cost.subDecomposed': '(decomposed into children)',
  'cost.kind.subassembly': 'sub-assy',
  'cost.kind.leaf': 'leaf · L{{level}}',
  'cost.wo.title': 'Work Order Cost — Estimated vs Actual',
  'cost.col.estimated': 'Standard',
  'cost.col.actual': 'Actual',
  'cost.col.variance': 'Variance',

  'inv.title': 'Inventory + WIP',
  'inv.intro': 'Material LOT balances by location / movement & consumption history / count accuracy (target 99.5%) / WIP material issue progress.',
  'inv.kpi.accuracy': 'Inventory Accuracy (30d)',
  'inv.kpi.accuracyHint': 'Target {{target}}% · {{n}} samples',
  'inv.kpi.availLots': 'Available LOTs',
  'inv.kpi.value': 'Inventory Value',
  'inv.kpi.locations': 'Locations',
  'inv.balance.title': 'Balance',
  'inv.filter.all': 'All',
  'inv.balance.empty': 'No LOT balance at this location.',
  'inv.col.lot': 'LOT',
  'inv.col.item': 'Item',
  'inv.col.location': 'Location',
  'inv.col.status': 'Status',
  'inv.col.initial': 'Initial',
  'inv.col.onHand': 'On Hand',
  'inv.col.value': 'Value',
  'inv.consumedPct': '({{pct}}% consumed)',
  'inv.action.count': 'Count',
  'inv.wip.title': 'WIP — Issued vs BOM Required',
  'inv.wip.empty': 'No work orders in progress.',
  'inv.wip.bomMissing': 'BOM undefined',
  'inv.wip.col.material': 'Material',
  'inv.wip.col.required': 'Required',
  'inv.wip.col.issued': 'Issued',
  'inv.wip.col.variance': 'Variance',
  'inv.wip.col.pct': '%',
  'inv.wip.col.status': 'Status',
  'inv.txn.title': 'Last 30 Days Transactions',
  'inv.txn.empty': 'No transactions.',
  'inv.txn.col.time': 'Time',
  'inv.txn.col.type': 'Type',
  'inv.txn.col.qty': 'Qty',
  'inv.txn.col.note': 'Note',
  'inv.txn.consume': 'consumed',
  'inv.count.modal': 'Count — {{lot}}',
  'inv.count.systemQty': '{{item}} · System qty {{qty}} {{uom}}',
  'inv.count.field': 'Counted qty ({{uom}})',
  'inv.count.action': 'Save Count',
  'inv.count.result': 'Accuracy {{pct}}% · variance {{variance}}',
}
